import * as z from "zod/mini";

import {message_id_schema} from "./message_id.ts";
import {
    group_setting_value_schema,
    left_sidebar_category_schema,
    topic_link_schema,
    user_badge_info_schema,
} from "./types.ts";

export const topic_settings_event_schema = z.object({
    id: z.number(),
    type: z.literal("topic_settings"),
    stream_ids: z.array(z.number()),
    invalidate_messages: z.boolean(),
});
export type TopicSettingsEvent = z.infer<typeof topic_settings_event_schema>;

const avatar_overlay_event_schema = z.object({
    url: z.string(),
    size: z.number(),
    x: z.number(),
    y: z.number(),
    opacity: z.number(),
    z: z.number(),
    breakout: z.string(),  // "false", "true", or "all"
    rot: z.number(),  // rotation in degrees
    desc: z.string(),  // description shown as tooltip
});

// Tippy.js props for custom avatar tooltip
const tippy_props_event_schema = z.object({
    content: z.optional(z.string()),
    arrow: z.optional(z.boolean()),
    placement: z.optional(z.string()),
    theme: z.optional(z.string()),
    animation: z.optional(z.string()),
    delay: z.optional(z.string()),
    duration: z.optional(z.string()),
    interactive: z.optional(z.boolean()),
    trigger: z.optional(z.string()),
    hideOnClick: z.optional(z.string()),
    maxWidth: z.optional(z.string()),
    allowHTML: z.optional(z.boolean()),
});

// TippyClickInject data for injecting content into user card popover
const tippyclick_inject_event_schema = z.object({
    loc: z.string(),  // "top", "header", or "bottom"
    content: z.string(),  // HTML content to inject
});

export const user_group_update_event_schema = z.object({
    id: z.number(),
    type: z.literal("user_group"),
    op: z.literal("update"),
    group_id: z.number(),
    data: z.object({
        name: z.optional(z.string()),
        description: z.optional(z.string()),
        can_add_members_group: z.optional(group_setting_value_schema),
        can_join_group: z.optional(group_setting_value_schema),
        can_leave_group: z.optional(group_setting_value_schema),
        can_manage_group: z.optional(group_setting_value_schema),
        can_mention_group: z.optional(group_setting_value_schema),
        can_remove_members_group: z.optional(group_setting_value_schema),
        deactivated: z.optional(z.boolean()),
    }),
});
export type UserGroupUpdateEvent = z.output<typeof user_group_update_event_schema>;

export const update_message_event_schema = z.object({
    id: z.number(),
    type: z.literal("update_message"),
    user_id: z.nullable(z.number()),
    rendering_only: z.boolean(),
    message_id: message_id_schema,
    message_ids: z.array(message_id_schema),
    flags: z.array(z.string()),
    edit_timestamp: z.number(),
    stream_name: z.optional(z.string()),
    stream_id: z.optional(z.number()),
    new_stream_id: z.optional(z.number()),
    propagate_mode: z.optional(z.string()),
    orig_subject: z.optional(z.string()),
    subject: z.optional(z.string()),
    topic_links: z.optional(z.array(topic_link_schema)),
    orig_content: z.optional(z.string()),
    orig_rendered_content: z.optional(z.string()),
    content: z.optional(z.string()),
    rendered_content: z.optional(z.string()),
    is_me_message: z.optional(z.boolean()),
    message_badges: z.optional(z.array(user_badge_info_schema)),
    breakpage_html: z.optional(z.nullable(z.string())),
    breakpage_viewers: z.optional(z.nullable(z.object({
        all_access: z.boolean(),
        user_ids: z.array(z.number()),
        group_ids: z.array(z.number()),
    }))),
    left_sidebar_categories: z.optional(z.array(left_sidebar_category_schema)),
    content_placeholders: z.optional(z.array(z.string())),
    wiki_editable: z.boolean(),
    avatar_overlays: z.optional(z.array(avatar_overlay_event_schema)),
    tippy_props: z.optional(z.nullable(tippy_props_event_schema)),
    tippyclick_props: z.optional(z.nullable(tippy_props_event_schema)),
    tippyclick_inject: z.optional(z.nullable(tippyclick_inject_event_schema)),
    // The server is still using subject.
    // This will not be set until it gets fixed.
    topic: z.optional(z.string()),
});
export type UpdateMessageEvent = z.output<typeof update_message_event_schema>;

export const message_details_schema = z.record(
    z.coerce.number<string>(),
    z.intersection(
        z.object({mentioned: z.optional(z.boolean())}),
        z.discriminatedUnion("type", [
            z.object({type: z.literal("private"), user_ids: z.array(z.number())}),
            z.object({
                type: z.literal("stream"),
                stream_id: z.number(),
                topic: z.string(),
                unmuted_stream_msg: z.boolean(),
            }),
        ]),
    ),
);
export type MessageDetails = z.output<typeof message_details_schema>;

export const channel_folder_update_event_schema = z.object({
    id: z.number(),
    type: z.literal("channel_folder"),
    op: z.literal("update"),
    channel_folder_id: z.number(),
    data: z.object({
        name: z.optional(z.string()),
        description: z.optional(z.string()),
        rendered_description: z.optional(z.string()),
        is_archived: z.optional(z.boolean()),
    }),
});
export type ChannelFolderUpdateEvent = z.output<typeof channel_folder_update_event_schema>;
